import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type ThemeMode = 'light' | 'dark' | 'warm';

interface ThemeContextValue {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  isDarkMode: boolean;
  isFocusMode: boolean;
  toggleFocusMode: () => void;
  disableFocusMode: () => void;
}

const THEME_MODE_KEY = 'pref:colorMode';
const FOCUS_MODE_KEY = 'pref:focusMode';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getInitialTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_MODE_KEY);
  if (stored === 'dark' || stored === 'warm' || stored === 'light') return stored;
  try {
    const prefersDark =
      window.matchMedia && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
        : false;
    return prefersDark ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(getInitialTheme);
  const [isFocusMode, setIsFocusMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(FOCUS_MODE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(THEME_MODE_KEY, themeMode);
      } catch { /* ignore */ }
    }
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('theme-dark', themeMode === 'dark');
      document.body.classList.toggle('theme-light', themeMode === 'light');
      document.body.classList.toggle('theme-warm', themeMode === 'warm');
      document.body.classList.toggle('dark-mode', themeMode === 'dark');
      document.body.classList.toggle('redshift-mode', themeMode === 'warm');
    }
  }, [themeMode]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('focus-mode', isFocusMode);
    }
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(FOCUS_MODE_KEY, isFocusMode ? '1' : '0');
      } catch { /* ignore */ }
    }
  }, [isFocusMode]);

  const toggleFocusMode = useCallback(() => setIsFocusMode((prev) => !prev), []);
  const disableFocusMode = useCallback(() => setIsFocusMode(false), []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      themeMode,
      setThemeMode,
      isDarkMode: themeMode === 'dark',
      isFocusMode,
      toggleFocusMode,
      disableFocusMode,
    }),
    [themeMode, isFocusMode, toggleFocusMode, disableFocusMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};
